import type { Epic, ProductGoal } from '@mona-roadmap/domain';
import { ExternalLink, Plus } from 'lucide-react';
import { fieldKey } from '../../lib/fieldState';
import { formatTime } from '../../lib/format';
import { isTempId } from '../../lib/optimistic';
import { DateField, PeopleField, SelectField } from '../cells/Editors';
import { useRoadmapContext } from '../RoadmapContext';
import { Field, FieldError, Sheet, TextCommitField } from './parts';

const MondayLink = ({ url }: { url: string }) =>
  url ? (
    <a className="inline-flex items-center gap-1 text-accent-ink hover:underline" href={url} target="_blank" rel="noopener noreferrer">
      Open in monday <ExternalLink className="size-3.5" aria-hidden />
    </a>
  ) : null;

export function GoalDrawer({ goal: g, onClose, onCloseFocus, onAddDeliverable }: { goal: ProductGoal; onClose: () => void; onCloseFocus: () => void; onAddDeliverable: () => void }) {
  const { metadata, mutations } = useRoadmapContext();
  const key = (f: string) => fieldKey('g', g.id, f);
  const pending = isTempId(g.id);
  return (
    <Sheet
      title={g.name}
      subtitle="Product Goal"
      onClose={onClose}
      onCloseFocus={onCloseFocus}
      footer={
        <div className="flex items-center justify-between">
          <span>{g.updatedAt ? `Updated ${formatTime(g.updatedAt)}` : ''}</span>
          <MondayLink url={g.url} />
        </div>
      }
    >
      <TextCommitField label="Name" value={g.name} stateKey={key('name')} disabled={pending} onCommit={(name) => void mutations.patchGoal(g.id, { name })} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Target date">
          <DateField block field="Target date" value={g.targetDate} stateKey={key('targetDate')} readOnly={pending} onChange={(v) => void mutations.patchGoal(g.id, { targetDate: v })} />
          <FieldError stateKey={key('targetDate')} />
        </Field>
        <Field label="Status">
          <SelectField block field="Goal status" style="chip" value={g.status} options={metadata.options.pgStatus} stateKey={key('status')} readOnly={pending} onChange={(v) => void mutations.patchGoal(g.id, { status: v })} clearLabel="No status" />
          <FieldError stateKey={key('status')} />
        </Field>
      </div>
      <button type="button" className="btn" onClick={onAddDeliverable} disabled={pending}>
        <Plus className="size-3.5" aria-hidden /> Add deliverable to this goal
      </button>
    </Sheet>
  );
}

export function EpicDrawer({ epic: e, onClose, onCloseFocus, onAddDeliverable }: { epic: Epic; onClose: () => void; onCloseFocus: () => void; onAddDeliverable: () => void }) {
  const { metadata, mutations } = useRoadmapContext();
  const key = (f: string) => fieldKey('e', e.id, f);
  const pending = isTempId(e.id);
  return (
    <Sheet
      title={e.name}
      subtitle="Epic"
      onClose={onClose}
      onCloseFocus={onCloseFocus}
      footer={
        <div className="flex items-center justify-between">
          <span>{e.updatedAt ? `Updated ${formatTime(e.updatedAt)}` : ''}</span>
          <MondayLink url={e.url} />
        </div>
      }
    >
      <TextCommitField label="Name" value={e.name} stateKey={key('name')} disabled={pending} onCommit={(name) => void mutations.patchEpic(e.id, { name })} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Product Area" note="Deliverables of this epic inherit this value.">
          <SelectField block field="Product Area" style="text" value={e.productArea} options={metadata.options.productArea} stateKey={key('productArea')} readOnly={pending} onChange={(v) => void mutations.patchEpic(e.id, { productArea: v })} clearLabel="No product area" />
          <FieldError stateKey={key('productArea')} />
        </Field>
        <Field label="Type">
          <SelectField block field="Type" style="text" value={e.type} options={metadata.options.type} stateKey={key('type')} readOnly={pending} onChange={(v) => void mutations.patchEpic(e.id, { type: v })} clearLabel="No type" />
          <FieldError stateKey={key('type')} />
        </Field>
        <Field label="PM Owner">
          <PeopleField block field="PM Owner" people={e.pmOwners} all={metadata.people} stateKey={key('pmOwnerIds')} readOnly={pending} onCommit={(ids) => void mutations.patchEpic(e.id, { pmOwnerIds: ids })} />
          <FieldError stateKey={key('pmOwnerIds')} />
        </Field>
        <Field label="R&D owner">
          <PeopleField block field="R&D owner" people={e.rdOwners} all={metadata.people} stateKey={key('rdOwnerIds')} readOnly={pending} onCommit={(ids) => void mutations.patchEpic(e.id, { rdOwnerIds: ids })} />
          <FieldError stateKey={key('rdOwnerIds')} />
        </Field>
      </div>
      <button type="button" className="btn" onClick={onAddDeliverable} disabled={pending}>
        <Plus className="size-3.5" aria-hidden /> Add deliverable to this epic
      </button>
    </Sheet>
  );
}
